import {
  applySorting,
  createFileRowHTML,
  filterFilesByStatus,
  formatDate,
  formatFullDate,
  getFileActions,
} from "./library.js";
import {
  fuzzyScore,
  highlightDisplayName,
  highlightSnippet,
} from "./search.js";

export function createLibraryView({
  document,
  window,
  dummyFiles,
  libraryViewElement = document.getElementById("libraryView"),
  vendingMachineElement = document.getElementById("vendingMachine"),
  transcriptElements,
  getTranscriptElements = null,
  numberFormatter,
  contentSearchMinLength,
  contentSearchMaxFiles,
  loadTranscriptContent,
  getDisplayName,
  hideTranscriptView,
  updateDemoButtons,
  applyFullLibrary,
  viewTranscript,
  getTranscriptSearchHotkeys = () => null,
}) {
  const fileListElements = {
    wrapper: document.getElementById("fileListScroll"),
    viewport: document.querySelector("#fileListScroll [data-scroll-viewport]"),
    list: document.getElementById("fileList"),
  };
  let fileListScrollbar = null;

  let currentFilter = "all";
  let searchMode = "files";
  let searchTerm = "";
  const contentHits = new Map();
  let pendingInDocSearchPreset = null;

  function escapeAttributeValue(value) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/'/g, "&#39;");
  }

  function sortFiles() {
    renderFileList();
  }

  function renderFileList() {
    const fileList = document.getElementById("fileList");
    const emptyState = document.getElementById("emptyState");
    const fileListWrapper = document.getElementById("fileListScroll");

    const filteredFiles = filterFilesByStatus(dummyFiles, currentFilter);
    let filesToRender = filteredFiles;

    const term = searchTerm;
    const termLength = term.length;
    const isContentMode = searchMode === "content";
    const hasFullContentQuery =
      isContentMode && termLength >= contentSearchMinLength;
    const isContentTermTooShort =
      isContentMode && termLength > 0 && termLength < contentSearchMinLength;

    if (term && !isContentMode) {
      // search on displayName (user-facing title) rather than internal name (job id)
      const scoredFiles = filteredFiles
        .map((file) => ({
          file,
          score: fuzzyScore(file.displayName || file.name, term),
        }))
        .filter((entry) => entry.score > 0)
        .sort((a, b) => b.score - a.score);

      filesToRender = scoredFiles.map((entry) => entry.file);
    } else if (hasFullContentQuery) {
      filesToRender = filteredFiles.filter((file) =>
        contentHits.has(file.name),
      );
    } else if (isContentMode) {
      filesToRender = filteredFiles.filter(
        (file) => file.status === "complete",
      );
    }

    const sortedFiles = hasFullContentQuery
      ? filesToRender.slice().sort((a, b) => {
          const hitA = contentHits.get(a.name);
          const hitB = contentHits.get(b.name);
          const indexA = hitA ? hitA.index : Number.MAX_SAFE_INTEGER;
          const indexB = hitB ? hitB.index : Number.MAX_SAFE_INTEGER;
          return indexA - indexB;
        })
      : applySorting(filesToRender, document);

    if (sortedFiles.length === 0) {
      if (fileListWrapper) fileListWrapper.style.display = "none";
      emptyState.style.display = "block";

      const emptyText = emptyState.querySelector(".empty-state-text");
      const emptyHint = emptyState.querySelector(".empty-state-hint");

      if (isContentTermTooShort) {
        emptyText.textContent = "Keep typing";
        emptyHint.textContent = `Enter at least ${contentSearchMinLength} characters to search transcripts.`;
      } else if (term) {
        emptyText.textContent = "No matches";
        emptyHint.textContent = isContentMode
          ? "Try another transcript keyword."
          : "Try another filename.";
      } else if (currentFilter === "all") {
        emptyText.textContent = "No files yet";
        emptyHint.textContent = "Drop a file above to start";
      } else if (currentFilter === "completed") {
        emptyText.textContent = "No completed items";
        emptyHint.textContent = "Check in progress";
      } else if (currentFilter === "in_progress") {
        emptyText.textContent = "No files processing";
        emptyHint.textContent = "Upload a new file to see it here";
      } else if (currentFilter === "failed") {
        emptyText.textContent = "No failed uploads";
        emptyHint.textContent = "Failed files will appear here";
      }
    } else {
      if (fileListWrapper) fileListWrapper.style.display = "block";
      emptyState.style.display = "none";
      fileList.innerHTML = sortedFiles
        .map((file) =>
          createFileRowHTML(file, {
            searchMode,
            searchTerm,
            contentHits,
            contentSearchMinLength,
            escapeAttributeValue,
            getDisplayName,
            numberFormatter,
          }),
        )
        .join("");
      applyFileListHeightConstraint();
    }
    if (typeof updateFailedTabGlow === "function") updateFailedTabGlow();
  }

  function refreshFileRow(file) {
    if (!file) return false;

    const fileList = document.getElementById("fileList");
    if (!fileList) return false;

    const row = fileList.querySelector(`.file-row[data-file-id="${file.id}"]`);
    if (!row) return false;

    row.dataset.fileId = String(file.id);

    const nameEl = row.querySelector(".file-name");
    if (nameEl) {
      const displayName = getDisplayName(file.name);
      if (searchMode === "files" && searchTerm) {
        nameEl.innerHTML = highlightDisplayName(displayName, searchTerm);
      } else {
        nameEl.textContent = displayName;
      }
    }

    const statusPill = row.querySelector(".status-pill");
    if (statusPill) {
      statusPill.textContent = file.status;
      statusPill.className = `status-pill status-${file.status}`;
    }

    const actionsContainer = row.querySelector(".file-actions");
    if (actionsContainer) {
      actionsContainer.innerHTML = getFileActions(file)
        .map(
          (action) =>
            `<button class="action-btn ${action.class}" onclick="${action.onclick}">${action.text}</button>`,
        )
        .join("");
    }

    const meta = row.querySelector(".file-meta");
    if (meta) {
      const hasNumericProgress =
        typeof file.progress === "number" && !Number.isNaN(file.progress);
      const clampedProgress = hasNumericProgress
        ? Math.max(0, Math.min(100, file.progress))
        : null;

      if (file.status === "queued") {
        meta.innerHTML =
          '<div class="progress-bar progress-bar--indeterminate" data-status="queued"></div>';
      } else if (file.status === "transcribing") {
        if (
          clampedProgress != null &&
          clampedProgress >= 0 &&
          clampedProgress <= 100
        ) {
          let progressBar = meta.querySelector(".progress-bar");
          if (
            !progressBar ||
            progressBar.classList.contains("progress-bar--indeterminate")
          ) {
            meta.innerHTML =
              '<div class="progress-bar progress-bar--transcribing"><div class="progress-fill--transcribing"></div></div>';
            progressBar = meta.querySelector(".progress-bar");
          }
          const fill = progressBar.querySelector(
            ".progress-fill--transcribing",
          );
          if (fill) {
            fill.style.width = `${clampedProgress}%`;
          }
        } else {
          meta.innerHTML =
            '<div class="progress-bar progress-bar--indeterminate" data-status="transcribing"></div>';
        }
      } else if (
        file.status !== "complete" &&
        clampedProgress != null &&
        clampedProgress < 100
      ) {
        let progressBar = meta.querySelector(".progress-bar");
        if (!progressBar) {
          meta.innerHTML =
            '<div class="progress-bar"><div class="progress-fill"></div></div>';
          progressBar = meta.querySelector(".progress-bar");
        }

        const fill = progressBar.querySelector(".progress-fill");
        if (fill) {
          fill.style.width = `${clampedProgress}%`;
        }
      } else if (file.status === "complete") {
        meta.textContent = `created: ${formatFullDate(file.created)}`;
      } else if (file.status === "failed") {
        meta.textContent = `failed ${formatDate(file.created)}`;
      } else {
        meta.textContent = `uploaded ${formatDate(file.created)}`;
      }
    }

    if (file.status === "complete") {
      row.setAttribute("data-filename", file.name);
      row.setAttribute("onclick", "handleFileRowClick(event)");
    } else {
      row.removeAttribute("data-filename");
      row.removeAttribute("onclick");
    }

    return true;
  }

  function applyFileListHeightConstraint() {
    const wrapper = fileListElements.wrapper;
    const viewport = fileListElements.viewport;
    const list = fileListElements.list;
    if (!wrapper || !viewport || !list) return;

    const rows = list.querySelectorAll(".file-row");
    if (rows.length === 0) {
      wrapper.style.height = "auto";
      if (typeof fileListScrollbar !== "undefined" && fileListScrollbar)
        fileListScrollbar.refresh();
      return;
    }

    const maxRows = 9;
    if (rows.length > maxRows) {
      const first = rows[0];
      const last = rows[maxRows - 1];
      const style = getComputedStyle(list);
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      const firstTop = first.offsetTop;
      const lastBottom = last.offsetTop + last.offsetHeight;
      let target = paddingTop + (lastBottom - firstTop) + paddingBottom;
      target = Math.ceil(target / 4) * 4;
      wrapper.style.height = `${target}px`;
    } else {
      wrapper.style.height = "auto";
    }

    if (typeof fileListScrollbar !== "undefined" && fileListScrollbar)
      fileListScrollbar.refresh();
  }

  function updateFailedTabGlow() {
    const hasFailed = dummyFiles.some((f) => f.status === "failed");
    const tabs = document.querySelectorAll(".filter-tab");
    const failedTab = tabs && tabs[3] ? tabs[3] : null;
    if (!failedTab) return;
    failedTab.classList.toggle("failed-glow", hasFailed);
  }

  function setFilter(filter) {
    currentFilter = filter;
    document
      .querySelectorAll(".filter-tab")
      .forEach((tab) => tab.classList.remove("active"));

    const filterMap = { all: 0, in_progress: 1, completed: 2, failed: 3 };
    const tabIndex = filterMap[filter];
    if (tabIndex !== undefined) {
      document
        .querySelectorAll(".filter-tab")
        [tabIndex].classList.add("active");
    }

    renderFileList();
  }

  function setSearchMode(mode) {
    if (mode !== "files" && mode !== "content") return;

    searchMode = mode;

    const container = document.querySelector(".library-controls .search-modes");
    if (container) {
      const chips = container.querySelectorAll(".mode-chip");
      chips.forEach((chip) => chip.classList.remove("active"));
      if (mode === "files" && chips[0]) {
        chips[0].classList.add("active");
      } else if (mode === "content" && chips[1]) {
        chips[1].classList.add("active");
      }
    }

    const input = document.querySelector(".library-controls .search-input");
    if (input) {
      input.placeholder =
        mode === "content"
          ? `Search transcript content... (type at least ${contentSearchMinLength} characters)`
          : "Search files... (switch scope below)";
    }

    if (mode !== "content") {
      contentHits.clear();
    }

    filterFiles(searchTerm);
  }

  async function searchContent(term) {
    if (!term || term.length < contentSearchMinLength) {
      return;
    }

    const completedFiles = dummyFiles
      .filter((file) => file.status === "complete")
      .slice(0, contentSearchMaxFiles);
    const lowerTerm = term.toLowerCase();
    const pendingHits = [];

    await Promise.all(
      completedFiles.map(async (file) => {
        const text = await loadTranscriptContent(file.name);
        if (searchTerm !== term) {
          return;
        }

        const lowerText = text.toLowerCase();
        const index = lowerText.indexOf(lowerTerm);

        if (index !== -1) {
          const snippet = highlightSnippet(text, term);
          if (snippet && searchTerm === term) {
            pendingHits.push({ name: file.name, index, snippet });
          }
        }
      }),
    );

    if (searchTerm !== term) {
      return;
    }

    contentHits.clear();
    pendingHits
      .sort((a, b) => a.index - b.index)
      .forEach((hit) => {
        contentHits.set(hit.name, {
          index: hit.index,
          snippet: hit.snippet,
        });
      });
  }

  async function filterFiles(term) {
    searchTerm = (term || "").trim();
    const activeTerm = searchTerm;

    if (searchMode === "content") {
      if (activeTerm.length >= contentSearchMinLength) {
        contentHits.clear();
        await searchContent(activeTerm);
        if (activeTerm !== searchTerm) {
          return;
        }
      } else {
        contentHits.clear();
      }
    } else {
      contentHits.clear();
    }

    renderFileList();
  }

  function maybeQueueInDocSearchPreset(filename, options = {}) {
    if (
      searchMode === "content" &&
      searchTerm &&
      searchTerm.length >= contentSearchMinLength &&
      filename &&
      contentHits.has(filename)
    ) {
      pendingInDocSearchPreset = {
        filename,
        term: searchTerm,
        focus:
          options && Object.prototype.hasOwnProperty.call(options, "focus")
            ? !!options.focus
            : true,
      };
    } else {
      pendingInDocSearchPreset = null;
    }
  }

  function applyPendingInDocSearchPreset(openedFilename) {
    const preset = pendingInDocSearchPreset;
    if (!preset || preset.filename !== openedFilename) {
      if (preset && preset.filename !== openedFilename) {
        pendingInDocSearchPreset = null;
      }
      return;
    }

    pendingInDocSearchPreset = null;

    const term = (preset.term || "").trim();
    if (!term) return;

    const focus = preset.focus === true;
    const hotkeys =
      (typeof getTranscriptSearchHotkeys === "function" &&
        getTranscriptSearchHotkeys()) ||
      (window && window.SearchHotkeys) ||
      null;
    if (hotkeys && typeof hotkeys.presetInDocSearch === "function") {
      hotkeys.presetInDocSearch(term, { focus, select: true });
    } else if (hotkeys && typeof hotkeys.showInDocBar === "function") {
      hotkeys.showInDocBar(term, { focus, select: true });
    }
  }

  function showVendingMachine() {
    const resolvedTranscriptElements =
      (typeof getTranscriptElements === "function" &&
        getTranscriptElements()) ||
      transcriptElements;
    if (vendingMachineElement) {
      vendingMachineElement.style.display = "flex";
      // attach wordmark vim-highlight animation if not already attached
      const wordmark = vendingMachineElement.querySelector(".vm-wordmark");
      if (wordmark && !wordmark.dataset.animationAttached) {
        wordmark.dataset.animationAttached = "true";
        const triggerHighlight = () => {
          wordmark.classList.add("vm-wordmark--highlighted");
          setTimeout(
            () => wordmark.classList.remove("vm-wordmark--highlighted"),
            120,
          );
        };
        wordmark.addEventListener("click", triggerHighlight);
        wordmark.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            triggerHighlight();
          }
        });
      }
    }
    if (libraryViewElement) {
      libraryViewElement.classList.remove("active");
      libraryViewElement.style.display = "none";
    }
    if (resolvedTranscriptElements && resolvedTranscriptElements.container) {
      resolvedTranscriptElements.container.classList.remove("active");
      resolvedTranscriptElements.container.style.display = "none";
      resolvedTranscriptElements.container.setAttribute("aria-hidden", "true");
    }
    document.body.classList.remove("library-mode");
    document.body.classList.add("first-time-mode");
    if (typeof hideTranscriptView === "function") {
      hideTranscriptView();
    }
    if (typeof updateDemoButtons === "function") {
      updateDemoButtons("vm");
    }
    if (window.DemoMode) {
      window.DemoMode.state.clearAll();
      window.DemoMode.state.set("first-time", true);
    }
  }

  function showLibrary() {
    const resolvedTranscriptElements =
      (typeof getTranscriptElements === "function" &&
        getTranscriptElements()) ||
      transcriptElements;
    if (vendingMachineElement) vendingMachineElement.style.display = "none";
    if (libraryViewElement) {
      libraryViewElement.classList.add("active");
      libraryViewElement.style.display = "flex";
    }
    if (resolvedTranscriptElements && resolvedTranscriptElements.container) {
      resolvedTranscriptElements.container.classList.remove("active");
      resolvedTranscriptElements.container.style.display = "none";
      resolvedTranscriptElements.container.setAttribute("aria-hidden", "true");
    }
    document.body.classList.remove("first-time-mode");
    document.body.classList.add("library-mode");
    if (typeof hideTranscriptView === "function") {
      hideTranscriptView();
    }
    if (
      window.DemoMode &&
      !window.DemoMode.enabled &&
      dummyFiles.length === 0
    ) {
      try {
        applyFullLibrary();
      } catch (_) {}
    }
    renderFileList();
    if (
      window.CustomScrollbars &&
      !fileListScrollbar &&
      fileListElements.wrapper &&
      fileListElements.viewport
    ) {
      fileListScrollbar = window.CustomScrollbars.enhance(
        fileListElements.wrapper,
        { viewport: fileListElements.viewport },
      );
    }
    if (fileListScrollbar) {
      requestAnimationFrame(() => fileListScrollbar.refresh());
    }
    if (typeof updateDemoButtons === "function") {
      updateDemoButtons("library");
    }

    if (libraryViewElement) {
      const title = libraryViewElement.querySelector(".library-title");
      if (title) {
        title.setAttribute("tabindex", "-1");
        title.focus({ preventScroll: true });
        requestAnimationFrame(() => title.removeAttribute("tabindex"));
      }
    }
    if (window.DemoMode) {
      window.DemoMode.state.clearAll();
      window.DemoMode.state.set("library-view", true);
    }
  }

  function handleFileRowClick(event) {
    const target = event.currentTarget;
    const filename = target && target.dataset ? target.dataset.filename : null;
    if (!filename) return;
    maybeQueueInDocSearchPreset(filename);
    viewTranscript(filename);
  }

  return {
    renderFileList,
    refreshFileRow,
    applyFileListHeightConstraint,
    updateFailedTabGlow,
    setFilter,
    setSearchMode,
    filterFiles,
    sortFiles,
    showVendingMachine,
    showLibrary,
    handleFileRowClick,
    maybeQueueInDocSearchPreset,
    applyPendingInDocSearchPreset,
    getSearchMode: () => searchMode,
    getSearchTerm: () => searchTerm,
    getContentHits: () => contentHits,
    getCurrentFilter: () => currentFilter,
    getLibraryViewElement: () => libraryViewElement,
  };
}
